import TooltipLink from "@/components/TooltipLink/TooltipLink";
import useTranslation from "@/hooks/useTranslation";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { message, Popconfirm, Tooltip } from "antd";
import { ReactNode } from "react";
import { Packaging } from "@/pages/packaging/PackagingOverview";
import { useDeletePackaging } from "@/pages/packaging/usePackagings";

export default function DeletePackagingAction({
  children,
  packagings,
  onSucces,
  deselectUndeletableProducts,
}: {
  children: ReactNode;
  packagings: Packaging[];
  onSucces?: () => void;
  deselectUndeletableProducts?: () => void;
}) {
  const deletePackagings = useDeletePackaging();

  const materialLabel = packagings.length === 1 ? "packaging" : `${packagings.length} packaging`;
  const deleteText = `Delete ${materialLabel}`;

  const disabled = packagings.some((x) => x.relations > 0);
  const undeletableCount = packagings.filter((x) => x.deletable).length;
  const translation = useTranslation();

  return (
    <Popconfirm
      title={deleteText}
      description={
        <>
          <p>Deleting a packaging also deletes:</p>
          <ul>
            <li>the BOM of this packaging</li>
          </ul>
          <p style={{ color: "#F5222D" }}>This action can not be undone.</p>
        </>
      }
      onConfirm={() =>
        deletePackagings
          .mutateAsync({ packagingIds: packagings.map((x) => x.id) })
          .then((result) => {
            if (result.status === 200) {
              message.success(`Packaging removed.`);
              onSucces && onSucces();
            }
          })
          .catch(() => message.error(translation.errors.general))
      }
      onCancel={() => {}}
      icon={<ExclamationCircleFilled style={{ color: "#F5222D" }} />}
      okText={deleteText}
      cancelText="Cancel"
      placement="bottom"
      okType="danger"
      okButtonProps={{ loading: deletePackagings.isLoading }}
      disabled={disabled}
    >
      <Tooltip
        placement="bottom"
        title={
          disabled &&
          (packagings.length === 1 ? (
            <p>
              This packaging can not be deleted as it is referenced in products. Delete these
              products first to remove this packaging.
            </p>
          ) : (
            <p>
              {undeletableCount} of the packaging you selected are referenced in a product.{" "}
              {deselectUndeletableProducts ? (
                <>
                  <TooltipLink onClick={() => deselectUndeletableProducts()}>
                    Click here
                  </TooltipLink>{" "}
                  to deselect them or remove the related product to continue.
                </>
              ) : (
                <>Delete the related product to continue.</>
              )}
            </p>
          ))
        }
      >
        {children}
      </Tooltip>
    </Popconfirm>
  );
}
